import { useUserPreferences } from '@core/hooks';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FavoriteMenu } from './FavoriteMenu';
import * as styles from './Guide.css';

/**
 * Props for the GuideFavoritesSection component
 * @interface GuideFavoritesSectionProps
 */
interface GuideFavoritesSectionProps {
  /** Callback to close the Guide panel after navigation */
  onClose: () => void;
}

/**
 * GuideFavoritesSection component
 * Displays the user's favorited pages as a dedicated tab in the Guide
 * Each favorite has a menu with options to set as home page or remove from favorites
 */
export function GuideFavoritesSection({ onClose }: GuideFavoritesSectionProps) {
  const navigate = useNavigate();
  const { favorites, homePage, setHomePage, clearHomePage, removeFavorite } = useUserPreferences();

  // Track which menu is open
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleFavoriteClick = useCallback(
    (route: string) => {
      navigate(route);
      onClose();
    },
    [navigate, onClose]
  );

  const handleSetAsHome = useCallback(
    (route: string) => {
      setHomePage(route);
    },
    [setHomePage]
  );

  const handleRemoveFavorite = useCallback(
    (id: string) => {
      removeFavorite(id);
    },
    [removeFavorite]
  );

  const handleMenuOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenMenuId(isOpen ? id : null);
  }, []);

  // Show empty state if no favorites
  if (favorites.length === 0) {
    return (
      <div className={styles.emptyFavoritesState}>
        <p>No favorites yet</p>
        <p className={styles.emptyFavoritesHint}>Click the star icon on any page in a community to add it to your favorites.</p>
      </div>
    );
  }

  return (
    <div className={styles.favoritesList}>
      {favorites.map(favorite => {
        const isCurrentHome = homePage === favorite.route;

        return (
          <div key={favorite.id} className={styles.favoriteItemContainer}>
            {/* Navigation button */}
            <button
              aria-label={`Navigate to ${favorite.label}`}
              className={styles.favoriteItemButton}
              type="button"
              onClick={() => handleFavoriteClick(favorite.route)}
            >
              <span className={styles.favoriteItemLabel}>{favorite.label}</span>
            </button>

            {/* Actions menu (triple dots) */}
            <FavoriteMenu
              favoriteId={favorite.id}
              favoriteLabel={favorite.label}
              isHomePage={isCurrentHome}
              isOpen={openMenuId === favorite.id}
              onClearHome={clearHomePage}
              onOpenChange={isOpen => handleMenuOpenChange(favorite.id, isOpen)}
              onRemove={() => handleRemoveFavorite(favorite.id)}
              onSetAsHome={() => handleSetAsHome(favorite.route)}
            />
          </div>
        );
      })}
    </div>
  );
}
